import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ManagerLayout from './ManagerLayout';
import { RefreshCw, AlertCircle, Clock, CheckCircle } from 'lucide-react';

export default function ManagerDashboard() {
  const [manager, setManager] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Get manager profile
      const { data: mgr } = await supabase
        .from('managers')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      if (!mgr) { setLoading(false); return; }
      setManager(mgr);

      // Get assigned companies
      const { data: assigns } = await supabase
        .from('company_manager_assignments')
        .select('*, companies(*)')
        .eq('manager_id', mgr.id)
        .eq('is_active', true);

      setCompanies((assigns || []).map(a => a.companies).filter(Boolean));

      // Get all tickets for this manager
      const { data: ticketsData } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('manager_id', mgr.id)
        .order('created_at', { ascending: false });

      setTickets(ticketsData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !manager) {
    return (
      <ManagerLayout manager={manager}>
        <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
          Loading...
        </div>
      </ManagerLayout>
    );
  }

  const openTickets = tickets.filter(t =>
    ['open', 'in_progress', 'waiting_company'].includes(t.status));
  const resolvedTickets = tickets.filter(t => t.status === 'resolved');
  const urgentTickets = tickets.filter(t =>
    t.priority === 'urgent' && t.status !== 'resolved');

  return (
    <ManagerLayout manager={{ ...manager, current_companies: companies.length }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B' }}>
          Welcome back, {manager.name.split(' ')[0]}! 👋
        </h1>
        <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 4 }}>
          {manager.tier === 'enterprise' ? 'Enterprise Support Dashboard' : 'Support Manager Dashboard'}
          {' · '}{new Date().toLocaleDateString('en-US', {
            weekday: 'long', month: 'long', day: 'numeric'
          })}
        </p>
      </div>

      {/* Urgent alert */}
      {urgentTickets.length > 0 && (
        <div style={{
          background: '#FFF1F2', border: '1.5px solid #FECDD3',
          borderRadius: 12, padding: '14px 18px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <AlertCircle size={18} color="#EF4444" />
          <div>
            <strong style={{ color: '#9F1239' }}>
              {urgentTickets.length} urgent ticket{urgentTickets.length > 1 ? 's' : ''} need attention
            </strong>
            <div style={{ fontSize: 12, color: '#BE123C', marginTop: 2 }}>
              {urgentTickets.map(t => t.title).join(' · ')}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
        gap: 14, marginBottom: 28,
      }}>
        {[
          { l: 'Assigned Companies', v: companies.length, max: manager.max_companies,
            i: '🏢', c: '#4F8EF7' },
          { l: 'Open Tickets', v: openTickets.length, i: '🎫', c: '#F59E0B' },
          { l: 'Urgent', v: urgentTickets.length, i: '🚨', c: '#EF4444' },
          { l: 'Resolved', v: resolvedTickets.length, i: '✅', c: '#10B981' },
        ].map((s, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 14, padding: '18px 20px',
            border: '1px solid #E2E8F0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <span style={{ fontSize: 26 }}>{s.i}</span>
              <div>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.c }}>{s.v}</div>
                <div style={{ fontSize: 11, color: '#94A3B8' }}>
                  {s.l}{s.max ? ` / ${s.max}` : ''}
                </div>
              </div>
            </div>
            {s.max && (
              <div style={{ height: 4, background: '#F1F5F9', borderRadius: 2, marginTop: 10 }}>
                <div style={{
                  height: '100%', borderRadius: 2,
                  background: s.c,
                  width: `${Math.min(100, (s.v / s.max) * 100)}%`,
                }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Recent tickets */}
        <div style={{
          background: '#fff', borderRadius: 14,
          border: '1px solid #E2E8F0', overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px', borderBottom: '1px solid #F1F5F9',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
              Recent Tickets
            </div>
            <span style={{ fontSize: 12, color: '#94A3B8' }}>
              {openTickets.length} open
            </span>
          </div>
          {tickets.slice(0, 6).map(t => {
            const isUrgent = t.priority === 'urgent';
            const isOpen = ['open', 'in_progress'].includes(t.status);
            return (
              <div key={t.id} style={{
                padding: '14px 20px', borderBottom: '1px solid #F1F5F9',
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: isUrgent ? '#EF4444' : isOpen ? '#F59E0B' : '#10B981',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: '#1E293B',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t.title}
                  </div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>
                    {t.submitted_by_name} · {new Date(t.created_at).toLocaleDateString()}
                    {isUrgent && <span style={{ color: '#EF4444', fontWeight: 700 }}> · URGENT</span>}
                  </div>
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  background: isUrgent ? '#FFF1F2' : isOpen ? '#FEF3C7' : '#D1FAE5',
                  color: isUrgent ? '#EF4444' : isOpen ? '#F59E0B' : '#10B981',
                }}>
                  {t.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            );
          })}
          {tickets.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
              No tickets yet
            </div>
          )}
        </div>

        {/* Assigned companies */}
        <div style={{
          background: '#fff', borderRadius: 14,
          border: '1px solid #E2E8F0', overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px', borderBottom: '1px solid #F1F5F9',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
              Assigned Companies
            </div>
            <span style={{ fontSize: 12, color: '#94A3B8' }}>
              {companies.length}/{manager.max_companies}
            </span>
          </div>
          {companies.slice(0, 6).map(c => (
            <div key={c.id} style={{
              padding: '12px 20px', borderBottom: '1px solid #F1F5F9',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 800, fontSize: 14, flexShrink: 0,
              }}>
                {c.name[0]}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700, color: '#1E293B',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {c.name}
                </div>
                <div style={{ fontSize: 11, color: '#94A3B8' }}>
                  {c.city}, {c.country} · {c.verification_status}
                </div>
              </div>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: c.is_verified ? '#10B981' : '#F59E0B',
                display: 'inline-block',
              }} />
            </div>
          ))}
          {companies.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
              No companies assigned yet
            </div>
          )}
        </div>
      </div>
    </ManagerLayout>
  );
}